import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { PartnerTransferSection } from 'further-types/lp/partner-transfer';
import api from 'lib/trpcClient';
import { PartnerTransfersFormValues } from './usePartnerTransfersForm';
import { ActionRefLabels } from 'further-ui/labels';

type UseLoadPartnerTransferActionsParams = {
  partnerTransferId: string;
  vehicleId: string;
  partnerTransferSection: PartnerTransferSection;
};

export default function useLoadPartnerTransferActions({
  partnerTransferId,
  vehicleId,
  partnerTransferSection,
}: UseLoadPartnerTransferActionsParams) {
  const { setValue, watch } = useFormContext<PartnerTransfersFormValues>();

  const [actionAllocations] =
    api.partnerTransfers.getActionAllocations.useSuspenseQuery(
      {
        partnerTransferId,
        vehicleId,
        partnerTransferSection,
      },
      {
        staleTime: 0,
        gcTime: 0,
      },
    );

  useEffect(() => {
    const transformedActions =
      actionAllocations.map((action) => ({
        actionId: action.actionId,
        actionRef: action.actionRef,
        actionType: ActionRefLabels[action.actionRef],
        counterpartyLabel: action.counterpartyLabel,
        ledgerDate: action.ledgerDate,
        totalAmountLcy: action.totalAmountLcy,
        allocationRule: action.allocationRule,
        originalAllocations: action.originalAllocations.map((allocation) => ({
          ...allocation,
          closeDate: allocation.closeDate.toUTCDate(),
          allocationAmountLcy: allocation.allocationAmountLcy ?? 0,
        })),
        newAllocations: action.newAllocations.map((allocation) => ({
          ...allocation,
          closeDate: allocation.closeDate.toUTCDate(),
          allocationAmountLcy: allocation.allocationAmountLcy ?? 0,
        })),
        linkedActions: action.linkedActions.map((linkedAction) => ({
          ...linkedAction,
          actionId: linkedAction.actionId,
          actionRef: linkedAction.actionRef,
          actionType: ActionRefLabels[linkedAction.actionRef],
          originalAllocations: linkedAction.originalAllocations.map(
            (allocation) => ({
              ...allocation,
              closeDate: allocation.closeDate.toUTCDate(),
              allocationAmountLcy: allocation.allocationAmountLcy ?? 0,
            }),
          ),
          newAllocations: linkedAction.newAllocations.map((allocation) => ({
            ...allocation,
            closeDate: allocation.closeDate.toUTCDate(),
            allocationAmountLcy: allocation.allocationAmountLcy ?? 0,
          })),
          originalRealisationAllocations:
            linkedAction.originalRealisationAllocations,
          newRealisationAllocations: linkedAction.newRealisationAllocations,
          newRealisationAllocationTotals:
            linkedAction.newRealisationAllocationTotals,
        })),
      })) ?? [];

    // Get current actions array from form
    const currentActions = watch('actions') ?? [];

    // Find if section already exists in the actions array
    const sectionIndex = currentActions.findIndex(
      (item) => item.section === partnerTransferSection,
    );

    if (sectionIndex >= 0) {
      // Update existing section
      setValue(`actions.${sectionIndex}.actions`, transformedActions, {
        shouldValidate: true,
      });
    } else {
      // Add new section
      setValue(
        'actions',
        [
          ...currentActions,
          {
            section: partnerTransferSection,
            actions: transformedActions,
          },
        ],
        { shouldValidate: true },
      );
    }
  }, [actionAllocations, partnerTransferSection, setValue, watch]);

  // Return the actions for this specific section
  const allActions = watch('actions') ?? [];
  const sectionActions =
    allActions.find((item) => item.section === partnerTransferSection)
      ?.actions ?? [];

  return {
    actions: sectionActions,
  };
}
